/**
 * Competitor-NAMED grounding-search matching.
 *
 * The sibling of `site-scope.ts`, for the behavior that predates the `site:`
 * operator: a model that wants a specific brand's take often just names it
 * ("CompetitorX pricing", "asana vs monday review") instead of scoping the
 * search. Those fan-outs are the same kind of dead end a `site:competitor.com`
 * query is — we cannot rank for a search that asks about someone else's
 * product, so buying a SERP task for one flatters nothing and scores as a
 * "high-impact gap" nobody can ever close.
 *
 * THE PREDICATE IS COMPETITOR-ONLY. A query that names a competitor AND our
 * brand ("Acme vs CompetitorX") is a comparison query — rankable, trackable,
 * and one of the most valuable searches we have. Only `namesCompetitor &&
 * !namesOurBrand` is excluded anywhere.
 *
 * Everything here is PURE — no database, no network — so it is safe on both the
 * server barrel and `@repo/core/client`. Classification is deliberately NOT
 * persisted: it depends on the CURRENT competitor list, so removing a
 * competitor un-excludes their queries instantly with no backfill, and a
 * historical row is never stuck behind a flag written under an old list.
 *
 * Matching is FORGIVING about spelling, because fan-outs are machine-written
 * and rarely punctuate a name the way the brand does: "Ritz Carlton" has to hit
 * "The Ritz-Carlton Residences, Miami Beach", and "St Regis" has to hit "St.
 * Regis Residences". Both sides are normalized to bare lowercase tokens (see
 * {@link normalizeNameText}), a comma-separated name contributes its
 * brand-bearing head, and the words before a category word ("Residences",
 * "Hotel", "Inc") give a shorter alias. What is never generated is a term
 * made only of generic or place words ("Miami Beach", "Residences"): those
 * would exclude ordinary category searches wholesale.
 *
 * Accepted residual risk: a competitor literally named a common word ("Monday")
 * over-excludes some ordinary queries. Whole-token matching and the 3-character
 * term floor are the whole guard — best-effort string matching is the agreed
 * bar, and read-time evaluation makes every mistake reversible.
 */

import { normalizeDomainOrNull } from "./domain";

/**
 * The shortest term this module will match on.
 *
 * Three characters, the brand-mention-scan convention — one character above the
 * floor `wordBoundaryMatch` enforces for `detectPromptClass`, which legitimately
 * matches 2-character aliases because it only ever runs against a prompt the
 * user wrote. Here the input is machine-generated fan-out text and a 2-character
 * competitor alias ("HP", "GM") would silently exclude a large slice of it.
 */
const MIN_TERM_LENGTH = 3;

/**
 * A single-token alias DERIVED by trimming a name ("Faena Residences" ->
 * "faena") has to be at least this long. Shorter heads ("Una Residences" ->
 * "una") are ordinary words in some language far too often to stand alone; the
 * full name still matches.
 */
const MIN_DERIVED_SINGLE_TOKEN = 4;

/**
 * Below this a joined label ("ritzcarlton") is not compared against adjacent
 * query tokens run together. Short labels joined across tokens collide with
 * ordinary word pairs far too easily.
 */
const MIN_JOINED_LABEL_LENGTH = 6;

/** The longest run of adjacent query tokens joined for label matching. */
const MAX_JOINED_TOKENS = 4;

/**
 * Hostname-shaped tokens: letters, digits, dots and dashes, nothing else.
 *
 * Used to decide whether a NAME should also contribute its bare second-level
 * label. "asana.com" as a competitor name should match the plain query "asana
 * pricing"; "Acme, Inc." must not be mistaken for a host just because it has a
 * dot in it.
 */
const HOSTNAME_SHAPE = /^[a-z0-9.-]+$/i;

/**
 * A `site:` operator and its argument, with or without a leading `-`.
 *
 * Stripped before matching, because an operator argument is a DIRECTIVE about
 * where to search, not part of what the search is about. Without this,
 * `best crm -site:competitor.com` — an ordinary open-web query that explicitly
 * excludes a competitor, and one we can absolutely rank for — would read as
 * competitor-named and be dropped. Positively-scoped queries lose nothing by
 * being stripped here: every caller runs the site-scope check first, so they
 * are already gone.
 *
 * Safe as a module constant despite the `g` flag: `String.replace` resets
 * `lastIndex` itself, unlike the `exec` loop in `site-scope.ts`.
 */
const SITE_OPERATOR_TOKEN = /(^|\s)-?site:\s?\S+/gi;

/**
 * Category words a brand name is often built around. The words before the
 * first of them are the shorter form people actually search ("Faena
 * Residences" -> "faena", "Acme Group" -> "acme").
 */
const TRAILING_CATEGORY_WORDS = new Set([
	"residences",
	"residence",
	"hotel",
	"hotels",
	"condos",
	"condo",
	"condominium",
	"condominiums",
	"tower",
	"towers",
	"group",
	"inc",
	"llc",
	"ltd",
	"corp",
	"company",
]);

/**
 * Words that carry no brand identity on their own: articles, corporate
 * suffixes, category words, web plumbing and common place words. A term made
 * ONLY of these is never generated, because it would match ordinary category
 * searches ("miami beach condos") rather than a brand.
 *
 * Deliberately a short, conservative list. A place it misses ("Brickell") can
 * still be a term on its own only if a competitor or alias is literally named
 * that, which is the user's list to curate.
 */
const GENERIC_NAME_WORDS = new Set([
	...TRAILING_CATEGORY_WORDS,
	// Articles, prepositions and joiners, English plus the common Romance ones.
	"the",
	"a",
	"an",
	"and",
	"of",
	"at",
	"by",
	"in",
	"on",
	"for",
	"de",
	"del",
	"la",
	"le",
	"el",
	"los",
	"las",
	"du",
	"des",
	"y",
	"et",
	// Corporate and web plumbing.
	"co",
	"corporation",
	"limited",
	"holdings",
	"plc",
	"gmbh",
	"llp",
	"com",
	"net",
	"org",
	"io",
	"www",
	"official",
	"website",
	"site",
	// Category words that show up inside names without identifying anyone.
	"luxury",
	"resort",
	"resorts",
	"suites",
	"apartments",
	"homes",
	"club",
	"collection",
	"international",
	"global",
	"new",
	// Common place words.
	"miami",
	"beach",
	"florida",
	"fl",
	"york",
	"ny",
	"city",
	"angeles",
	"san",
	"francisco",
	"london",
	"paris",
	"dubai",
	"usa",
	"us",
	"uk",
	"united",
	"states",
	"america",
	"north",
	"south",
	"east",
	"west",
	"central",
	"downtown",
	"midtown",
	"uptown",
	"bay",
	"harbor",
	"harbour",
	"island",
	"islands",
	"park",
	"district",
	"county",
	"state",
	"st",
	"saint",
	"coast",
	"port",
	"lake",
	"river",
	"hills",
	"heights",
	"village",
	"center",
	"centre",
]);

/**
 * Normalize a name or a query to bare lowercase tokens: NFKC, lowercase,
 * every run of punctuation (hyphens, dots, commas, apostrophes, ampersands)
 * and whitespace collapsed to a single space. Both sides of every comparison
 * go through this, so "The Ritz-Carlton" and "ritz carlton" meet in the middle.
 */
export function normalizeNameText(raw: string | null | undefined): string {
	return (raw ?? "")
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[^\p{L}\p{N}]+/gu, " ")
		.trim();
}

/** `normalizeNameText` plus a leading "the " stripped, for a NAME (not a query). */
function normalizeNameTerm(raw: string | null | undefined): string {
	return normalizeNameText(raw).replace(/^the\s+/, "");
}

/**
 * True when every token of an already-normalized term is a generic or place
 * word, i.e. the term names nobody in particular.
 */
export function isGenericOnlyTerm(normalized: string): boolean {
	const tokens = normalized.split(" ").filter(Boolean);
	return (
		tokens.length === 0 || tokens.every((t) => GENERIC_NAME_WORDS.has(t))
	);
}

/**
 * The brand-bearing head of a name: everything before its first category word
 * ("faena residences" -> "faena", "kempinski residences miami design district"
 * -> "kempinski", "acme software group" -> "acme software"). Null when the name
 * has no category word, or starts with one ("Hotel Indigo").
 */
function brandHead(normalized: string): string | null {
	const tokens = normalized.split(" ").filter(Boolean);
	const end = tokens.findIndex((token) => TRAILING_CATEGORY_WORDS.has(token));
	if (end <= 0) return null;
	const head = tokens.slice(0, end);
	if (head.length === 1 && head[0].length < MIN_DERIVED_SINGLE_TOKEN) {
		return null;
	}
	return head.join(" ");
}

/**
 * A hostname's normalized host and its bare second-level label.
 *
 * The split is deliberately the naive two-label one rather than the
 * public-suffix-aware `registrableLabel` in `site-scope.ts` (which is private
 * there, and duplicating its suffix table here would be a second thing to keep
 * current). A miss fails SAFE: `acme.co.uk` yields "co", which is two
 * characters and dies on {@link MIN_TERM_LENGTH} — a competitor we fail to
 * recognize, never a query we wrongly exclude.
 */
function hostTerms(raw: string | null | undefined): string[] {
	const host = normalizeDomainOrNull(raw);
	// A dot-less host ("localhost", or a `peer:jane-doe` pseudo-url that
	// normalizes to nothing usable) is not a domain we can take a label from.
	if (!host?.includes(".")) return [];
	const labels = host.split(".");
	return [host, labels[labels.length - 2] ?? ""];
}

/**
 * The raw terms a brand/alias string contributes, before normalization.
 *
 * A domain-shaped name expands to its host and label. Anything else
 * contributes itself and, when it is comma-separated ("The Ritz-Carlton
 * Residences, Miami Beach"), its brand-bearing head part: the first part that
 * names somebody rather than a place.
 */
function nameTerms(raw: string | null | undefined): string[] {
	const trimmed = (raw ?? "").trim();
	if (!trimmed) return [];
	if (HOSTNAME_SHAPE.test(trimmed) && trimmed.includes(".")) {
		return hostTerms(trimmed);
	}
	const terms = [trimmed];
	if (trimmed.includes(",")) {
		const head = trimmed
			.split(",")
			.map((part) => part.trim())
			.find((part) => !isGenericOnlyTerm(normalizeNameTerm(part)));
		if (head) terms.push(head);
	}
	return terms;
}

/**
 * Normalize, expand and filter a list of raw terms: every term normalized,
 * plus its brand-bearing head, minus anything below the floor or
 * made only of generic/place words. Deduped, in first-seen order.
 */
function expandTerms(rawTerms: string[]): string[] {
	const out: string[] = [];
	const seen = new Set<string>();
	const push = (term: string | null) => {
		if (!term || term.length < MIN_TERM_LENGTH) return;
		if (isGenericOnlyTerm(term) || seen.has(term)) return;
		seen.add(term);
		out.push(term);
	};
	for (const raw of rawTerms) {
		const normalized = normalizeNameTerm(raw);
		push(normalized);
		// A host ("widgets.co.uk") is not a name with a category word in it.
		const isHost = HOSTNAME_SHAPE.test(raw.trim()) && raw.includes(".");
		if (!isHost) push(brandHead(normalized));
	}
	return out;
}

/**
 * The normalized terms one competitor (or our own brand) is matched on: its
 * name, every alias and its url's host and label, expanded and filtered as
 * {@link expandTerms} describes.
 */
export function brandMatchTerms(entry: {
	name?: string | null;
	aliases?: Array<string | null | undefined> | null;
	url?: string | null;
}): string[] {
	return expandTerms([
		...nameTerms(entry.name),
		...(entry.aliases ?? []).flatMap((alias) => nameTerms(alias)),
		...hostTerms(entry.url),
	]);
}

export interface CompetitorNamedQueryContext {
	/**
	 * Our brand's own terms — company name, aliases, and the property domain.
	 * A domain contributes both its host and its bare second-level label, so
	 * passing `acme.com` covers "acme" too; passing either form is enough.
	 */
	ourBrandTerms: Array<string | null | undefined>;
	competitors: Array<{
		id: string;
		name: string | null;
		aliases?: string[] | null;
		url?: string | null;
	}>;
}

export interface CompetitorNamedQueryMatch {
	namesCompetitor: boolean;
	namesOurBrand: boolean;
	/** Canonical names of the competitors the query named. */
	matchedCompetitorNames: string[];
	/**
	 * `namesCompetitor && !namesOurBrand` — THE exclusion predicate. Branch on
	 * this and nothing else, so no two surfaces can disagree about what counts
	 * as a competitor-named search.
	 */
	competitorOnly: boolean;
}

/**
 * A compiled matcher. Cheap to call — every term is normalized once by the
 * builder, because the rankings service runs this over up to
 * EXECUTION_SCAN_CAP rows.
 */
export type CompetitorNamedQueryMatcher = (
	query: string | null | undefined,
) => CompetitorNamedQueryMatch;

const NO_MATCH: CompetitorNamedQueryMatch = {
	namesCompetitor: false,
	namesOurBrand: false,
	matchedCompetitorNames: [],
	competitorOnly: false,
};

/** A compiled term set: padded phrases for whole-token tests, plus joinable labels. */
interface CompiledTerms {
	phrases: string[];
	labels: string[];
}

function compileTerms(terms: string[]): CompiledTerms {
	return {
		phrases: terms.map((term) => ` ${term} `),
		labels: terms.filter(
			(term) =>
				!term.includes(" ") && term.length >= MIN_JOINED_LABEL_LENGTH,
		),
	};
}

/** A normalized query, ready for whole-token and joined-label tests. */
interface PreparedQuery {
	padded: string;
	joined: Set<string>;
}

function prepareQuery(normalized: string): PreparedQuery {
	const tokens = normalized.split(" ").filter(Boolean);
	// Adjacent tokens run together, so a single-word label ("ritzcarlton")
	// matches the spaced spelling ("ritz carlton") without matching across
	// arbitrary substrings ("beehive" never contains "hive").
	const joined = new Set<string>();
	for (let i = 0; i < tokens.length; i++) {
		let run = tokens[i];
		for (
			let j = i + 1;
			j < Math.min(tokens.length, i + MAX_JOINED_TOKENS);
			j++
		) {
			run += tokens[j];
			joined.add(run);
		}
	}
	return { padded: ` ${normalized} `, joined };
}

function matches(query: PreparedQuery, terms: CompiledTerms): boolean {
	return (
		terms.phrases.some((phrase) => query.padded.includes(phrase)) ||
		terms.labels.some((label) => query.joined.has(label))
	);
}

/**
 * Build a matcher that says whether a grounding search names a tracked
 * competitor, our own brand, or both.
 *
 * Context-injected and precompiled for the same reason
 * `buildSiteScopeClassifier` is: the caller loads the property + competitors
 * ONCE and the returned function runs inside a per-execution loop.
 *
 * Competitor terms are the canonical name, every alias, and — because a plain
 * named query says "asana pricing", not "asana.com pricing" — the competitor
 * url's host AND its bare second-level label.
 *
 * @example
 * const match = buildCompetitorNamedQueryMatcher({
 *   ourBrandTerms: ["Acme", "acme.com"],
 *   competitors: [{ id: "c1", name: "Globex", url: "https://globex.com" }],
 * });
 * match("globex pricing").competitorOnly     // true  — excluded
 * match("acme vs globex").competitorOnly     // false — a comparison query, kept
 * match("best crm software").competitorOnly  // false — names nobody
 */
export function buildCompetitorNamedQueryMatcher(
	context: CompetitorNamedQueryContext,
): CompetitorNamedQueryMatcher {
	const ours = compileTerms(
		expandTerms(
			(context.ourBrandTerms ?? []).flatMap((term) => nameTerms(term)),
		),
	);

	const competitors: Array<{ name: string; terms: CompiledTerms }> = [];
	for (const competitor of context.competitors ?? []) {
		const terms = brandMatchTerms(competitor);
		if (terms.length === 0) continue;
		competitors.push({
			name:
				competitor.name?.trim() ||
				normalizeDomainOrNull(competitor.url) ||
				competitor.id,
			terms: compileTerms(terms),
		});
	}

	// Nothing to match against — every query is clean, and saying so up front
	// keeps the no-competitor case off the per-row path entirely.
	if (competitors.length === 0) return () => NO_MATCH;

	return (query) => {
		const text =
			typeof query === "string"
				? normalizeNameText(query.replace(SITE_OPERATOR_TOKEN, "$1"))
				: "";
		if (!text) return NO_MATCH;
		const prepared = prepareQuery(text);

		const matchedCompetitorNames: string[] = [];
		for (const competitor of competitors) {
			if (matches(prepared, competitor.terms)) {
				matchedCompetitorNames.push(competitor.name);
			}
		}

		const namesCompetitor = matchedCompetitorNames.length > 0;
		const namesOurBrand = matches(prepared, ours);

		return {
			namesCompetitor,
			namesOurBrand,
			matchedCompetitorNames,
			competitorOnly: namesCompetitor && !namesOurBrand,
		};
	};
}

/**
 * A predicate over search TERMS (single words or short phrases pulled out of a
 * query) that says "this term belongs to a rival, not to us".
 *
 * Used to keep competitor names out of everything that tells a writer what
 * their page must contain: the brief's must-include list, the exact-terms
 * check and the vocabulary-gap advice. Asking a brand to put "Ritz Carlton" on
 * its own page because a search named it is the opposite of what anybody
 * wants.
 *
 * A term is excluded when any of its tokens is a DISTINCTIVE token of a
 * competitor term (not a generic or place word, and not one of our own brand's
 * tokens), or when it runs together into a competitor's joined label.
 * Standalone four-digit years are excluded too: a year in a search is a
 * recency hint, not something the page has to say.
 */
export function buildRivalTermExclusion(input: {
	competitorTerms?: Array<string | null | undefined> | null;
	ourBrandTerms?: Array<string | null | undefined> | null;
}): (term: string) => boolean {
	const ourTokens = new Set(
		expandTerms(
			(input.ourBrandTerms ?? []).flatMap((term) => nameTerms(term)),
		).flatMap((term) => term.split(" ")),
	);
	const distinctive = new Set<string>();
	for (const raw of input.competitorTerms ?? []) {
		for (const token of normalizeNameText(raw).split(" ")) {
			if (
				token.length >= MIN_TERM_LENGTH &&
				!GENERIC_NAME_WORDS.has(token) &&
				!ourTokens.has(token)
			) {
				distinctive.add(token);
			}
		}
	}

	return (term: string) => {
		const normalized = normalizeNameText(term);
		if (!normalized) return false;
		if (isYearTerm(normalized)) return true;
		if (distinctive.size === 0) return false;
		const tokens = normalized.split(" ");
		if (tokens.some((token) => distinctive.has(token))) return true;
		return tokens.length > 1 && distinctive.has(tokens.join(""));
	};
}

/** A standalone four-digit year (1900–2099). */
export function isYearTerm(term: string): boolean {
	return /^(19|20)\d{2}$/.test(term.trim());
}
